// Per-branch mesh topology for a multi_region bundle; the runner fetches the ONNX itself.

import { loadBranchMesh, type MeshAssets } from "./loader";
import { parseNpyFloat32 } from "../../lib/npyFloat32.js";
import { parseSceneManifest, type SceneManifest, type BranchSpec } from "./sceneManifest";

export interface BranchAssets {
  name: string;
  spec: BranchSpec;
  mesh: MeshAssets;
  uvRes: number;
  rigDim: number;
  /**
   * The rig the branch's `neutral_vertices` were exported at — row 0 of its rig params,
   * training's reference pose. Null only when the bundle ships none.
   *
   * This is NOT a zero vector and cannot be replaced by one. A myra head's control space
   * is 3 + 7 per bone (root translation, then a parent-relative quaternion and a scale per
   * bone — myra_v2_build_rig.py), so its rest value is a UNIT QUATERNION per bone. Zeroing
   * it hands the trunk a vector tens of units from anything it trained on and the code
   * latent comes out far wider than the decoder ever saw: what renders is a recognisable
   * face smeared by oversized, over-stretched splats.
   */
  referenceRig: Float32Array | null;
}

export interface MultiRegionScene {
  manifest: SceneManifest;
  branches: BranchAssets[]; // in manifest.order
  numPoses: number;
}

/** Row 0 of the branch's declared rig params, or null when it declares none. */
async function loadReferenceRig(base: string, spec: BranchSpec): Promise<Float32Array | null> {
  if (!spec.rigParams) return null;
  try {
    const response = await fetch(`${base}/${spec.rigParams}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const { data, rows, cols } = parseNpyFloat32(new Uint8Array(await response.arrayBuffer()));
    if (!rows) throw new Error(`${spec.rigParams} has no rows`);
    // WIDTH MUST MATCH the control space. A narrower row would be zero-padded downstream,
    // re-introducing the very off-distribution rig this reference exists to avoid.
    if (cols !== spec.rigDim) {
      throw new Error(`${spec.rigParams} is ${cols} controls, branch rig_dim is ${spec.rigDim}`);
    }
    return data.slice(0, cols);
  } catch (cause) {
    // Labelled, never silent: the caller falls back to a zero rig, which decodes an
    // off-distribution neutral rather than nothing at all, so the reason has to be visible
    // rather than inferred from a wrong-looking face. Uploading the file is the fix.
    console.warn(`[ogs] branch '${spec.name}': cannot read '${spec.rigParams}' — decoding the `
      + "neutral at an all-zero rig, which is NOT this branch's rest pose", cause);
    return null;
  }
}

/** Skips gaze-conditioned branches: mountEyeBranch consumes their spec directly. */
export async function loadMultiRegionScene(base: string, sceneJson: unknown): Promise<MultiRegionScene> {
  const manifest = parseSceneManifest(sceneJson);
  const branches: BranchAssets[] = [];
  for (const name of manifest.order) {
    const spec = manifest.branches[name];
    if (spec.gazeConditioned) continue;
    const mesh = await loadBranchMesh(base, spec.meshJson!, spec.meshBin!);
    const referenceRig = await loadReferenceRig(base, spec);
    branches.push({ name, spec, mesh, uvRes: spec.uvRes, rigDim: spec.rigDim, referenceRig });
  }
  return { manifest, branches, numPoses: manifest.numPoses };
}
